import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import countryCodes from "../data/countryCodes";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  
  // États pour le formulaire
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    phone: "", 
    password: "",
    confirmPassword: "",
    nom_societe: "",
    address_societe: "",
    tel_societe: "", 
    email_societe: "",
    nif_societe: "",
    rccm_societe: "",
    code_postal_societe: "",
  });
  
  // États pour la validation des mots de passe en temps réel
  const [passwordValidation, setPasswordValidation] = useState({
    match: null, // null = pas encore vérifié, true = correspond, false = ne correspond pas
    strength: 0, // 0-4 pour la force du mot de passe
    message: ""
  });
  
  // États pour les fichiers
  const [files, setFiles] = useState({
    nif_file: null,
    rccm_file: null,
    logo_file: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: "", message: "", type: "" });
  
  // États pour les sélecteurs d'indicatif
  const [showCountryListPersonal, setShowCountryListPersonal] = useState(false);
  const [showCountryListSociete, setShowCountryListSociete] = useState(false);
  const [selectedCountryPersonal, setSelectedCountryPersonal] = useState(
    countryCodes?.find(c => c.code === "+227") || countryCodes?.[0] || { 
      code: "+227", 
      name: "Niger", 
      flag: "🇳🇪",
      phoneLength: 8,
      pattern: "^\\+227\\d{8}$"
    }
  );
  const [selectedCountrySociete, setSelectedCountrySociete] = useState(
    countryCodes?.find(c => c.code === "+227") || countryCodes?.[0] || { 
      code: "+227", 
      name: "Niger", 
      flag: "🇳🇪",
      phoneLength: 8,
      pattern: "^\\+227\\d{8}$"
    }
  );
  const [searchCountryPersonal, setSearchCountryPersonal] = useState("");
  const [searchCountrySociete, setSearchCountrySociete] = useState("");
  
  // Références pour fermer les dropdowns
  const countryListRefPersonal = useRef(null);
  const countryListRefSociete = useRef(null);

  // Couleurs
  const primaryColor = "#0B3C5D";
  const accentColor = "#4FC3F7";
  const bgColor = "#F5F7FA";
  const textColor = "#2E2E2E";
  const borderColor = "#e0e0e0";
  const cardBgColor = "#E3F2FD";

  // Fermer les dropdowns en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryListRefPersonal.current && !countryListRefPersonal.current.contains(event.target)) {
        setShowCountryListPersonal(false);
      }
      if (countryListRefSociete.current && !countryListRefSociete.current.contains(event.target)) {
        setShowCountryListSociete(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fonction pour vérifier les mots de passe en temps réel avec valeurs courantes
  const checkPasswordsInRealTime = (currentPassword = null, currentConfirmPassword = null) => {
    const password = currentPassword !== null ? currentPassword : formData.password;
    const confirmPassword = currentConfirmPassword !== null ? currentConfirmPassword : formData.confirmPassword;
    
    // Si les deux champs sont vides, réinitialiser
    if (!password && !confirmPassword) {
      setPasswordValidation({
        match: null,
        strength: 0,
        message: ""
      });
      return;
    }
    
    // Vérifier la force du mot de passe
    let strength = 0;
    if (password.length >= 6) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    // Vérifier la correspondance
    let match = null;
    let message = "";
    
    if (confirmPassword) {
      if (password === confirmPassword) {
        match = true;
        message = "✓ Les mots de passe correspondent";
      } else {
        match = false;
        message = "⚠ Les mots de passe ne correspondent pas";
      }
    } else if (password) {
      // Seulement le premier champ est remplis
      message = "Veuillez confirmer votre mot de passe";
    }
    
    setPasswordValidation({
      match,
      strength,
      message
    });
  };

  // Gestion des changements de champs texte
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Pour les champs téléphone, n'accepter que les chiffres
    if (name === "phone" || name === "tel_societe") {
      // Déterminer la longueur max selon le pays
      const maxLength = name === "phone" 
        ? (selectedCountryPersonal?.phoneLength || 15)
        : (selectedCountrySociete?.phoneLength || 15);
      
      // Garder seulement les chiffres
      const digitsOnly = value.replace(/\D/g, "");
      // Limiter selon la longueur du pays
      const limitedDigits = digitsOnly.substring(0, maxLength);
      
      setFormData(prev => ({
        ...prev,
        [name]: limitedDigits
      }));
    } else {
      // Mettre à jour le formulaire AVANT de vérifier les mots de passe
      setFormData(prev => {
        const newFormData = {
          ...prev,
          [name]: value
        };
        
        // Vérifier les mots de passe APRÈS la mise à jour
        if (name === "password" || name === "confirmPassword") {
          setTimeout(() => {
            checkPasswordsInRealTime(
              name === "password" ? value : newFormData.password,
              name === "confirmPassword" ? value : newFormData.confirmPassword
            );
          }, 0);
        }
        
        return newFormData;
      });
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    
    // Vérification en temps réel des mots de passe (pour les champs non-password)
    if (name !== "password" && name !== "confirmPassword") {
      checkPasswordsInRealTime();
    }
  };

  // Fonction pour extraire les chiffres d'un numéro
  const extractPhoneDigits = (phoneValue) => {
    if (!phoneValue) return "";
    // Enlève tout sauf les chiffres
    return phoneValue.replace(/\D/g, "");
  };

  // Fonction pour formater le numéro complet avec indicatif
  const formatFullPhoneNumber = (phoneDigits, countryCode = "+227") => {
    if (!phoneDigits) return "";
    // Retourne l'indicatif + les chiffres
    return countryCode + phoneDigits;
  };

  // Fonction pour générer un exemple de numéro
  const generatePhoneExample = (phoneLength) => {
    if (!phoneLength) return "";
    let example = "";
    for (let i = 0; i < phoneLength; i++) {
      example += (i % 10).toString();
    }
    return example;
  };

  // Gestion des fichiers
  const handleFileChange = (e) => {
    const { name, files: fileList } = e.target;
    const file = fileList[0];
    
    if (file) {
      setFiles(prev => ({ ...prev, [name]: file }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
      }
      if (errors.files) {
        setErrors(prev => ({ ...prev, files: null }));
      }
    }
  };
  
  // Logo (optionnel) : géré séparément pour afficher un aperçu immédiat.
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 3 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, logo_file: "Fichier trop volumineux (max 3 Mo)." }));
      return;
    }

    setErrors(prev => ({ ...prev, logo_file: null }));
    setFiles(prev => ({ ...prev, logo_file: file }));

    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  // Validation des fichiers
  const validateFiles = () => {
    const newErrors = {};
    const allowedTypes = [
      'application/pdf', 
      'image/jpeg', 
      'image/png', 
      'image/jpg',
      'application/octet-stream'
    ];
    const maxSize = 5 * 1024 * 1024;
    
    if (!files.nif_file && !files.rccm_file) {
      newErrors.files = "Au moins un document (NIF ou RCCM) est obligatoire";
    }
    
    if (files.nif_file) {
      if (!allowedTypes.includes(files.nif_file.type)) {
        newErrors.nif_file = `Type de fichier non autorisé. Formats acceptés : PDF, JPEG, PNG`;
      }
      if (files.nif_file.size > maxSize) {
        newErrors.nif_file = `Fichier trop volumineux (${formatFileSize(files.nif_file.size)}). Taille maximale : 5MB`;
      }
    }
    
    if (files.rccm_file) {
      if (!allowedTypes.includes(files.rccm_file.type)) {
        newErrors.rccm_file = `Type de fichier non autorisé. Formats acceptés : PDF, JPEG, PNG`;
      }
      if (files.rccm_file.size > maxSize) {
        newErrors.rccm_file = `Fichier trop volumineux (${formatFileSize(files.rccm_file.size)}). Taille maximale : 5MB`;
      }
    }
    
    return newErrors;
  };
  
  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    // Lettres (accentuées), espaces, tirets et apostrophes uniquement — pas de
    // chiffres ni de caractères spéciaux dans un nom/prénom.
    const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
    if (!formData.nom.trim()) {
      newErrors.nom = "Le nom est obligatoire";
    } else if (!nameRegex.test(formData.nom.trim())) {
      newErrors.nom = "Le nom ne doit contenir que des lettres.";
    }
    if (!formData.prenom.trim()) {
      newErrors.prenom = "Le prénom est obligatoire";
    } else if (!nameRegex.test(formData.prenom.trim())) {
      newErrors.prenom = "Le prénom ne doit contenir que des lettres.";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "L'email est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email invalide";
    }
    
    // Validation téléphone personnel - DYNAMIQUE PAR PAYS
    const phoneDigits = extractPhoneDigits(formData.phone);
    const phoneFullNumber = formatFullPhoneNumber(phoneDigits, selectedCountryPersonal?.code || "+227");
    const expectedLengthPersonal = selectedCountryPersonal?.phoneLength;
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Le téléphone est obligatoire";
    } else if (!/^\d+$/.test(phoneDigits)) {
      newErrors.phone = "Le numéro doit contenir uniquement des chiffres";
    } else if (expectedLengthPersonal && phoneDigits.length !== expectedLengthPersonal) {
      newErrors.phone = `Numéro ${selectedCountryPersonal?.name || ""} invalide (${phoneDigits.length} chiffres, besoin de ${expectedLengthPersonal} chiffres)`;
    } else if (!expectedLengthPersonal && (phoneDigits.length < 8 || phoneDigits.length > 15)) {
      newErrors.phone = `Numéro invalide (${phoneDigits.length} chiffres, doit être entre 8 et 15 chiffres)`;
    }
    
    // Validation téléphone société - DYNAMIQUE PAR PAYS
    const telSocieteDigits = extractPhoneDigits(formData.tel_societe);
    const telSocieteFullNumber = formatFullPhoneNumber(telSocieteDigits, selectedCountrySociete?.code || "+227");
    const expectedLengthSociete = selectedCountrySociete?.phoneLength;
    
    if (!formData.tel_societe.trim()) {
      newErrors.tel_societe = "Le téléphone de la société est obligatoire";
    } else if (!/^\d+$/.test(telSocieteDigits)) {
      newErrors.tel_societe = "Le numéro doit contenir uniquement des chiffres";
    } else if (expectedLengthSociete && telSocieteDigits.length !== expectedLengthSociete) {
      newErrors.tel_societe = `Numéro ${selectedCountrySociete?.name || ""} invalide (${telSocieteDigits.length} chiffres, besoin de ${expectedLengthSociete} chiffres)`;
    } else if (!expectedLengthSociete && (telSocieteDigits.length < 8 || telSocieteDigits.length > 15)) {
      newErrors.tel_societe = `Numéro invalide (${telSocieteDigits.length} chiffres, doit être entre 8 et 15 chiffres)`;
    }
    
    if (!formData.password) {
      newErrors.password = "Le mot de passe est obligatoire";
    } else if (formData.password.length < 6) {
      newErrors.password = "Minimum 6 caractères";
    }
    
    // Utiliser l'état de validation en temps réel pour l'erreur
    if (passwordValidation.match === false || formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    }
    
    if (!formData.nom_societe.trim()) newErrors.nom_societe = "Le nom de la société est obligatoire";
   
    
    if (!formData.email_societe.trim()) {
      newErrors.email_societe = "L'email de la société est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_societe)) {
      newErrors.email_societe = "Email société invalide";
    }
    
    if (!formData.nif_societe.trim()) newErrors.nif_societe = "Le NIF est obligatoire";
    if (!formData.rccm_societe.trim()) newErrors.rccm_societe = "Le RCCM est obligatoire";
    
    if (formData.email === formData.email_societe) {
      newErrors.email_societe = "L'email société doit être différent de l'email personnel";
    }
    
    const fileErrors = validateFiles();
    Object.assign(newErrors, fileErrors);
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Fonction pour ouvrir la modal
  const openModal = (title, message, type = "info") => {
    setModalContent({ title, message, type });
    setShowModal(true);
  };
  
  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    try {
      const url = `${API_BASE_URL?.replace(/\/+$/, "")}/auth/register.php`;
      
      const formDataToSend = new FormData();
      
      // Données texte
      formDataToSend.append("nom", formData.nom);
      formDataToSend.append("prenom", formData.prenom);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("matricule", formData.matricule);
      
      // Récupérer les chiffres du téléphone et ajouter l'indicatif
      const phoneDigits = extractPhoneDigits(formData.phone);
      const phoneFullNumber = formatFullPhoneNumber(phoneDigits, selectedCountryPersonal?.code || "+227");
      formDataToSend.append("phone", phoneFullNumber);
      
      formDataToSend.append("password", formData.password);
      formDataToSend.append("nom_societe", formData.nom_societe);
      formDataToSend.append("address_societe", formData.address_societe);
      
      // Récupérer les chiffres du téléphone société et ajouter l'indicatif
      const telSocieteDigits = extractPhoneDigits(formData.tel_societe);
      const telSocieteFullNumber = formatFullPhoneNumber(telSocieteDigits, selectedCountrySociete?.code || "+227");
      formDataToSend.append("tel_societe", telSocieteFullNumber);
      
      formDataToSend.append("email_societe", formData.email_societe);
      formDataToSend.append("nif_societe", formData.nif_societe);
      formDataToSend.append("rccm_societe", formData.rccm_societe);
      formDataToSend.append("code_postal_societe", formData.code_postal_societe || "");
      
      // Fichiers
      if (files.nif_file) {
        formDataToSend.append("nif_file", files.nif_file);
      }
      if (files.rccm_file) {
        formDataToSend.append("rccm_file", files.rccm_file);
      }
      if (files.logo_file) {
        formDataToSend.append("logo_file", files.logo_file);
      }

      // Log pour débogage
      console.log("Numéro personnel à envoyer:", phoneFullNumber);
      console.log("Numéro société à envoyer:", telSocieteFullNumber);
      console.log("Données envoyées au backend:", {
        nom: formData.nom,
        phone: phoneFullNumber,
        tel_societe: telSocieteFullNumber
      });
      
      const response = await axios.post(url, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      
      if (response.data.success) {
        openModal(
          "🎉 Inscription réussie !",
          `Un email de confirmation a été envoyé à ${formData.email}. Veuillez vérifier votre boîte de réception et suivre les instructions pour activer votre compte.`,
          "success"
        );
        
        // Réinitialiser le formulaire
        setFormData({
          nom: "", prenom: "", email: "", phone: "", password: "", confirmPassword: "",
          nom_societe: "", address_societe: "", tel_societe: "", email_societe: "",
          nif_societe: "", rccm_societe: "", code_postal_societe: "",
        });
        setFiles({ nif_file: null, rccm_file: null, logo_file: null });
        setLogoPreview(null);
        setPasswordValidation({ match: null, strength: 0, message: "" });
        
      } else {
        let serverErrors = {};
        
        if (response.data.errors && Array.isArray(response.data.errors)) {
          const errorMessages = response.data.errors.join("\n");
          openModal("❌ Erreur d'inscription", errorMessages, "error");
          
          // Mapper les erreurs aux champs
          response.data.errors.forEach(error => {
            const lowerError = error.toLowerCase();
            if (lowerError.includes("email utilisateur") || lowerError.includes("email existe")) {
              serverErrors.email = error;
            } else if (lowerError.includes("email société")) {
              serverErrors.email_societe = error;
            } else if (lowerError.includes("nif")) {
              lowerError.includes("fichier") ? serverErrors.nif_file = error : serverErrors.nif_societe = error;
            } else if (lowerError.includes("rccm")) {
              lowerError.includes("fichier") ? serverErrors.rccm_file = error : serverErrors.rccm_societe = error;
            } else if (lowerError.includes("document") || lowerError.includes("fichier")) {
              serverErrors.files = error;
            } else if (lowerError.includes("société") || lowerError.includes("nom société")) {
              serverErrors.nom_societe = error;
            } else if (lowerError.includes("téléphone") || lowerError.includes("phone")) {
              serverErrors.phone = error;
            } else if (lowerError.includes("tél société") || lowerError.includes("tel_societe")) {
              serverErrors.tel_societe = error;
            } else if (lowerError.includes("mot de passe")) {
              serverErrors.password = error;
            } else {
              serverErrors.general = error;
            }
          });
        } else {
          const errorMsg = response.data.message || "Erreur lors de l'inscription";
          openModal("❌ Erreur", errorMsg, "error");
          serverErrors.general = errorMsg;
        }
        
        setErrors(serverErrors);
      }
    } catch (error) {
      let errorMessage = "Une erreur s'est produite. Veuillez réessayer.";
      
      if (error.response) {
        const errorData = error.response.data;
        if (errorData?.errors && Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.join("\n");
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = `Erreur ${error.response.status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        errorMessage = "Pas de réponse du serveur. Vérifiez que le serveur est démarré et accessible.";
      } else {
        errorMessage = "Erreur de configuration: " + error.message;
      }
      
      openModal("❌ Erreur de connexion", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  
  /* ====== COMPOSANT COUNTRYSELECTOR SIMPLE ====== */
  const CountrySelector = ({ 
    showList, setShowList, selectedCountry, setSelectedCountry, 
    search, setSearch, listRef, disabled 
  }) => {
    // Filtrer les pays de manière SÉCURISÉE
    const filteredCountries = (countryCodes || []).filter(country => {
      if (!country) return false;
      
      const searchTerm = (search || "").toLowerCase();
      const countryName = String(country.name || country.label || "").toLowerCase();
      const countryCode = String(country.code || "").toLowerCase();
      
      return countryName.includes(searchTerm) || countryCode.includes(searchTerm);
    });

    return (
      <div ref={listRef} className="position-relative">
        <button
          type="button"
          className="btn d-flex align-items-center justify-content-between"
          onClick={() => setShowList(!showList)}
          disabled={disabled}
          style={{
            backgroundColor: '#F5F7FA',
            border: `1px solid ${borderColor}`,
            borderRadius: '8px',
            padding: '10px 12px',
            width: '140px',
            minHeight: '44px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1
          }}
        >
          <span className="me-2" style={{ fontSize: '1.2rem' }}>{selectedCountry?.flag || "🏳️"}</span>
          <span style={{ fontSize: '0.9rem', color: textColor, fontWeight: '500' }}>
            {selectedCountry?.code || "+227"}
          </span>
          <i className="bi bi-chevron-down ms-2" style={{ fontSize: '0.8rem', color: '#666' }}></i>
        </button>

        {showList && !disabled && (
          <div style={{
            position: 'absolute', top: '100%', left: '0', width: '300px', maxHeight: '300px',
            backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, overflow: 'hidden', marginTop: '4px'
          }}>
            <div className="p-2 border-bottom">
              <div className="input-group">
                <span className="input-group-text" style={{ 
                  backgroundColor: '#F5F7FA', borderColor: '#e0e0e0', padding: '8px 12px'
                }}>
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un pays..."
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ fontSize: '0.9rem', borderColor: '#e0e0e0', padding: '8px 12px' }}
                  autoFocus
                />
              </div>
            </div>

            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredCountries.map((country, index) => {
                if (!country) return null;
                
                return (
                  <button
                    key={index}
                    type="button"
                    className="btn btn-light text-start w-100 border-0 rounded-0"
                    onClick={() => {
                      if (country) {
                        setSelectedCountry(country);
                        setShowList(false);
                        setSearch("");
                      }
                    }}
                    style={{ 
                      padding: '10px 16px', 
                      borderBottom: '1px solid #f0f0f0', 
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <div className="d-flex align-items-center">
                      <span className="me-3" style={{ fontSize: '1.2rem', minWidth: '30px' }}>
                        {country?.flag || "🏳️"}
                      </span>
                      <div className="flex-grow-1">
                        <div style={{ fontWeight: '500' }}>
                          {country?.name || country?.label || "Pays"}
                        </div>
                        <small style={{ color: '#666' }}>{country?.code || ""}</small>
                        {country?.phoneLength && (
                          <small style={{ color: '#666', display: 'block' }}>
                            {country.phoneLength} chiffres
                          </small>
                        )}
                      </div>
                      {selectedCountry?.code === country?.code && <i className="bi bi-check text-primary"></i>}
                    </div>
                  </button>
                );
              })}
              
              {filteredCountries.length === 0 && (
                <div className="text-center py-3 text-muted" style={{ fontSize: '0.9rem' }}>
                  Aucun pays trouvé
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Composant pour la force du mot de passe
  const PasswordStrengthMeter = ({ strength }) => {
    const segments = [
      { label: "Très faible", color: "#f44336" },
      { label: "Faible", color: "#ff9800" },
      { label: "Moyen", color: "#ffc107" },
      { label: "Fort", color: "#4caf50" },
      { label: "Très fort", color: "#2e7d32" }
    ];
    
    return (
      <div className="mt-2">
        <div className="d-flex align-items-center mb-1">
          <div className="d-flex" style={{ flex: 1, gap: '4px' }}>
            {[0, 1, 2, 3].map((index) => (
              <div
                key={index}
                style={{
                  flex: 1,
                  height: '4px',
                  backgroundColor: index < strength ? segments[strength].color : '#e0e0e0',
                  borderRadius: '2px',
                  transition: 'background-color 0.3s ease'
                }}
              />
            ))}
          </div>
          <small style={{ 
            color: strength > 0 ? segments[strength].color : '#666', 
            marginLeft: '8px', 
            fontSize: '0.8rem',
            fontWeight: '500'
          }}>
            {strength > 0 ? segments[strength].label : "Force"}
          </small>
        </div>
      </div>
    );
  };

  // Rendu JSX
  return (
    <>
      <main style={{ backgroundColor: bgColor, minHeight: '100vh', padding: '20px 0' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-xl-10">
              {/* Header */}
              <div className="text-center mb-5">
               <img 
  src="/assets/img/secureLogo.png" 
  alt="SecureControle Logo" 
  style={{ 
    width: '80px',
    height: '80px',
    borderRadius: '20px',
    boxShadow: '0 4px 12px rgba(11, 60, 93, 0.2)',
    display: 'block',
    margin: '0 auto 1rem auto'
  }}
/>
                <h1 style={{ color: primaryColor, fontWeight: '700', fontSize: '2rem', marginBottom: '8px' }}>
                  SecureControle
                </h1>
              </div>

              <div className="card mb-4" style={{ 
                backgroundColor: 'white', border: `1px solid ${borderColor}`, borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(11, 60, 93, 0.1)', overflow: 'hidden'
              }}>
                <div className="card-header text-center py-4" style={{ 
                  backgroundColor: primaryColor, color: 'white', borderBottom: `3px solid ${accentColor}`
                }}>
                  <h5 className="card-title mb-2 fs-4" style={{ fontWeight: '600', color: 'white' }}>
                    <i className="bi bi-person-plus me-2"></i>
                    Création de compte Gestionnaire
                  </h5>
                  <p className="mb-0 opacity-90" style={{ fontSize: '0.95rem' }}>
                    Créez votre société et votre compte gestionnaire
                  </p>
                </div>
                
                <div className="card-body p-4 p-lg-5">
                  {errors.general && (
                    <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                      backgroundColor: '#ffebee', borderColor: '#f44336', color: '#c62828',
                      borderRadius: '8px', borderLeft: '4px solid #f44336'
                    }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '1.2rem' }}></i>
                        <div className="flex-grow-1">{errors.general}</div>
                        <button 
                          type="button" 
                          className="btn-close" 
                          onClick={() => setErrors(prev => ({ ...prev, general: null }))}
                        ></button>
                      </div>
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} encType="multipart/form-data">
                    {/* Informations personnelles */}
                   
<div className="mb-5">
  <div className="d-flex align-items-center mb-4">
    <div style={{ 
      width: '40px', height: '40px', backgroundColor: cardBgColor, color: primaryColor,
      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px'
    }}>
      <i className="bi bi-person-circle fs-5"></i>
    </div>
    <h5 style={{ color: primaryColor, fontWeight: '600', margin: 0 }}>
      Informations personnelles
    </h5>
  </div>
  
  <div className="row g-3">
    {/* Nom - 4 colonnes */}
    <div className="col-12 col-md-4">
      <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
        <i className="bi bi-person-fill me-1" style={{ color: accentColor }}></i>
        Nom <span className="text-danger">*</span>
      </label>
      <input
        type="text"
        className={`form-control ${errors.nom ? 'is-invalid' : ''}`}
        name="nom"
        value={formData.nom}
        onChange={handleChange}
        placeholder=""
        disabled={loading}
        style={{ 
          borderRadius: '8px', borderColor: errors.nom ? '#f44336' : borderColor,
          padding: '12px 16px', fontSize: '0.95rem'
        }}
      />
      {errors.nom && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.nom}</div>}
    </div>
    
    {/* Prénom - 4 colonnes */}
    <div className="col-12 col-md-4">
      <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
        <i className="bi bi-person-fill me-1" style={{ color: accentColor }}></i>
        Prénom <span className="text-danger">*</span>
      </label>
      <input
        type="text"
        className={`form-control ${errors.prenom ? 'is-invalid' : ''}`}
        name="prenom"
        value={formData.prenom}
        onChange={handleChange}
        placeholder=""
        disabled={loading}
        style={{ 
          borderRadius: '8px', borderColor: errors.prenom ? '#f44336' : borderColor,
          padding: '12px 16px', fontSize: '0.95rem'
        }}
      />
      {errors.prenom && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.prenom}</div>}
    </div>

    {/* Matricule - 4 colonnes */}
    <div className="col-12 col-md-4">
      <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
        <i className="bi bi-card-text me-1" style={{ color: accentColor }}></i>
        Matricule <span className="text-danger">*</span>
      </label>
      <input
        type="text"
        className={`form-control ${errors.matricule ? 'is-invalid' : ''}`}
        name="matricule"
        value={formData.matricule}
        onChange={handleChange}
        placeholder="Ex: GEST00123"
        disabled={loading}
        style={{ 
          borderRadius: '8px', 
          borderColor: errors.matricule ? '#f44336' : borderColor,
          padding: '12px 16px', 
          fontSize: '0.95rem'
        }}
      />
      {errors.matricule && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.matricule}</div>}
    </div>
    
    {/* Email personnel - 6 colonnes */}
    <div className="col-12 col-md-6">
      <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
        <i className="bi bi-envelope-fill me-1" style={{ color: accentColor }}></i>
        Email personnel <span className="text-danger">*</span>
      </label>
      <input
        type="email"
        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
        name="email"
        value={formData.email}
        onChange={handleChange}
        placeholder=""
        disabled={loading}
        style={{ 
          borderRadius: '8px', borderColor: errors.email ? '#f44336' : borderColor,
          padding: '12px 16px', fontSize: '0.95rem'
        }}
      />
      {errors.email && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.email}</div>}
    </div>
    
    {/* Téléphone personnel - 6 colonnes */}
    <div className="col-12 col-md-6">
      <div className="mb-3">
        <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
          <i className="bi bi-phone-fill me-1" style={{ color: accentColor }}></i>
          Téléphone personnel <span className="text-danger">*</span>
        </label>
        
        <div className="d-flex align-items-stretch" style={{ gap: '8px' }}>
          <CountrySelector
            showList={showCountryListPersonal}
            setShowList={setShowCountryListPersonal}
            selectedCountry={selectedCountryPersonal}
            setSelectedCountry={setSelectedCountryPersonal}
            search={searchCountryPersonal}
            setSearch={setSearchCountryPersonal}
            listRef={countryListRefPersonal}
            disabled={loading}
          />
          
          <div className="position-relative" style={{ flex: '1' }}>
            <input
              type="tel"
              className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder=""
              disabled={loading}
              maxLength={selectedCountryPersonal?.phoneLength || 15}
              inputMode="numeric"
              pattern="[0-9]*"
              style={{ 
                borderRadius: '8px',
                borderColor: errors.phone ? '#f44336' : borderColor,
                padding: '12px 16px',
                fontSize: '0.95rem',
                width: '100%',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
            />
          </div>
        </div>
        
        {errors.phone && (
          <div className="invalid-feedback d-block" style={{ fontSize: '0.85rem' }}>
            {errors.phone}
          </div>
        )}
        
        <small className="text-muted" style={{ fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
          Indicatif: {selectedCountryPersonal?.code || "+227"} • 
          {selectedCountryPersonal?.phoneLength && 
            ` ${selectedCountryPersonal.phoneLength} chiffres (ex: ${generatePhoneExample(selectedCountryPersonal.phoneLength)})`}
        </small>
      </div>
    </div>
    
    {/* Mot de passe - 6 colonnes */}
    <div className="col-12 col-md-6">
      <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
        <i className="bi bi-lock-fill me-1" style={{ color: accentColor }}></i>
        Mot de passe <span className="text-danger">*</span>
      </label>
      <input
        type="password"
        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
        name="password"
        value={formData.password}
        onChange={handleChange}
        placeholder="Minimum 6 caractères"
        disabled={loading}
        style={{ 
          borderRadius: '8px', 
          borderColor: errors.password ? '#f44336' : 
                      (formData.password && passwordValidation.strength > 2 ? '#4caf50' : borderColor),
          padding: '12px 16px', 
          fontSize: '0.95rem',
          transition: 'border-color 0.3s ease'
        }}
      />
      {errors.password && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.password}</div>}
      
      {formData.password && (
        <PasswordStrengthMeter strength={passwordValidation.strength} />
      )}
    </div>
    
    {/* Confirmation mot de passe - 6 colonnes */}
    <div className="col-12 col-md-6">
      <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
        <i className="bi bi-lock-fill me-1" style={{ color: accentColor }}></i>
        Confirmer le mot de passe <span className="text-danger">*</span>
      </label>
      <input
        type="password"
        className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
        name="confirmPassword"
        value={formData.confirmPassword}
        onChange={handleChange}
        placeholder=""
        disabled={loading}
        style={{ 
          borderRadius: '8px', 
          borderColor: errors.confirmPassword ? '#f44336' : 
                      (passwordValidation.match === true ? '#4caf50' : 
                       passwordValidation.match === false ? '#f44336' : borderColor),
          padding: '12px 16px', 
          fontSize: '0.95rem',
          transition: 'border-color 0.3s ease'
        }}
      />
      {errors.confirmPassword && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.confirmPassword}</div>}
      
      {passwordValidation.message && (
        <div className="mt-2" style={{ 
          display: 'flex', 
          alignItems: 'center',
          fontSize: '0.85rem',
          color: passwordValidation.match === true ? '#2e7d32' : 
                 passwordValidation.match === false ? '#f44336' : '#666'
        }}>
          {passwordValidation.match === true ? (
            <i className="bi bi-check-circle-fill me-2" style={{ color: '#4caf50' }}></i>
          ) : passwordValidation.match === false ? (
            <i className="bi bi-exclamation-circle-fill me-2" style={{ color: '#f44336' }}></i>
          ) : (
            <i className="bi bi-info-circle me-2" style={{ color: '#666' }}></i>
          )}
          {passwordValidation.message}
        </div>
      )}
    </div>
  </div>
</div>
                    
                    {/* Informations société */}
                    <div className="mb-5">
                      <div className="d-flex align-items-center mb-4">
                        <div style={{ 
                          width: '40px', height: '40px', backgroundColor: cardBgColor, color: primaryColor,
                          borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px'
                        }}>
                          <i className="bi bi-building fs-5"></i>
                        </div>
                        <h5 style={{ color: primaryColor, fontWeight: '600', margin: 0 }}>
                          Informations de votre société
                        </h5>
                      </div>
                      
                      <div className="row g-3">
                        <div className="col-12 col-md-6 col-lg-4">
                          <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
                            <i className="bi bi-building me-1" style={{ color: accentColor }}></i>
                            Nom société <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.nom_societe ? 'is-invalid' : ''}`}
                            name="nom_societe"
                            value={formData.nom_societe}
                            onChange={handleChange}
                            placeholder=""
                            disabled={loading}
                            style={{ 
                              borderRadius: '8px', borderColor: errors.nom_societe ? '#f44336' : borderColor,
                              padding: '12px 16px', fontSize: '0.95rem'
                            }}
                          />
                          {errors.nom_societe && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.nom_societe}</div>}
                        </div>
                        
                        <div className="col-12 col-md-6 col-lg-4">
                          <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
                            <i className="bi bi-envelope-fill me-1" style={{ color: accentColor }}></i>
                            Email société <span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className={`form-control ${errors.email_societe ? 'is-invalid' : ''}`}
                            name="email_societe"
                            value={formData.email_societe}
                            onChange={handleChange}
                            placeholder=""
                            disabled={loading}
                            style={{ 
                              borderRadius: '8px', borderColor: errors.email_societe ? '#f44336' : borderColor,
                              padding: '12px 16px', fontSize: '0.95rem'
                            }}
                          />
                          {errors.email_societe && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.email_societe}</div>}
                        </div>
                        
                        <div className="col-12 col-md-6 col-lg-4">
                          {/* Téléphone société */}
                          <div className="mb-3">
                            <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
                              <i className="bi bi-phone-fill me-1" style={{ color: accentColor }}></i>
                              Téléphone société <span className="text-danger">*</span>
                            </label>
                            
                            <div className="d-flex align-items-stretch" style={{ gap: '8px' }}>
                              <CountrySelector
                                showList={showCountryListSociete}
                                setShowList={setShowCountryListSociete}
                                selectedCountry={selectedCountrySociete}
                                setSelectedCountry={setSelectedCountrySociete}
                                search={searchCountrySociete}
                                setSearch={setSearchCountrySociete}
                                listRef={countryListRefSociete}
                                disabled={loading}
                              />
                              
                              <div className="position-relative" style={{ flex: '1' }}>
                                <input
                                  type="tel"
                                  className={`form-control ${errors.tel_societe ? 'is-invalid' : ''}`}
                                  name="tel_societe"
                                  value={formData.tel_societe}
                                  onChange={handleChange}
                                  placeholder=""
                                  disabled={loading}
                                  maxLength={selectedCountrySociete?.phoneLength || 15}
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  style={{ 
                                    borderRadius: '8px',
                                    borderColor: errors.tel_societe ? '#f44336' : borderColor,
                                    padding: '12px 16px',
                                    fontSize: '0.95rem',
                                    width: '100%',
                                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                                  }}
                                />
                              </div>
                            </div>
                            
                            {errors.tel_societe && (
                              <div className="invalid-feedback d-block" style={{ fontSize: '0.85rem' }}>
                                {errors.tel_societe}
                              </div>
                            )}
                            
                            {/* <small className="text-muted" style={{ fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                              Indicatif: {selectedCountrySociete?.code || "+227"} • 
                              {selectedCountrySociete?.phoneLength && 
                                ` ${selectedCountrySociete.phoneLength} chiffres (ex: ${generatePhoneExample(selectedCountrySociete.phoneLength)})`}
                            </small> */}
                          </div>
                        </div>
                        
                        <div className="col-12 col-md-6">
                          <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
                            <i className="bi bi-file-text-fill me-1" style={{ color: accentColor }}></i>
                            Numéro NIF <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.nif_societe ? 'is-invalid' : ''}`}
                            name="nif_societe"
                            value={formData.nif_societe}
                            onChange={handleChange}
                            placeholder=""
                            disabled={loading}
                            style={{ 
                              borderRadius: '8px', borderColor: errors.nif_societe ? '#f44336' : borderColor,
                              padding: '12px 16px', fontSize: '0.95rem'
                            }}
                          />
                          {errors.nif_societe && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.nif_societe}</div>}
                        </div>
                        
                        <div className="col-12 col-md-6">
                          <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
                            <i className="bi bi-file-text-fill me-1" style={{ color: accentColor }}></i>
                            Numéro RCCM <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.rccm_societe ? 'is-invalid' : ''}`}
                            name="rccm_societe"
                            value={formData.rccm_societe}
                            onChange={handleChange}
                            placeholder=""
                            disabled={loading}
                            style={{ 
                              borderRadius: '8px', borderColor: errors.rccm_societe ? '#f44336' : borderColor,
                              padding: '12px 16px', fontSize: '0.95rem'
                            }}
                          />
                          {errors.rccm_societe && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.rccm_societe}</div>}
                        </div>
                        
                        {/* Adresse + Code postal ALIGNÉS */}
                        <div className="col-12 col-md-8">
                          <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
                            <i className="bi bi-geo-alt-fill me-1" style={{ color: accentColor }}></i>
                            Adresse complète
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.address_societe ? 'is-invalid' : ''}`}
                            name="address_societe"
                            value={formData.address_societe}
                            onChange={handleChange}
                            placeholder="" 
                            disabled={loading}
                            style={{ 
                              borderRadius: '8px', borderColor: errors.address_societe ? '#f44336' : borderColor,
                              padding: '12px 16px', fontSize: '0.95rem'
                            }}
                          />
                          {errors.address_societe && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.address_societe}</div>}
                        </div>
                        
                        <div className="col-12 col-md-4">
                          <label className="form-label" style={{ color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>
                            <i className="bi bi-mailbox me-1" style={{ color: accentColor }}></i>
                            Code postal
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="code_postal_societe"
                            value={formData.code_postal_societe}
                            onChange={handleChange}
                            placeholder=""
                            disabled={loading}
                            style={{ 
                              borderRadius: '8px', borderColor: borderColor, padding: '12px 16px', fontSize: '0.95rem'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Documents */}
                    <div className="mb-5">
                      <div className="d-flex align-items-center mb-4">
                        <div style={{ 
                          width: '40px', height: '40px', backgroundColor: cardBgColor, color: primaryColor,
                          borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px'
                        }}>
                          <i className="bi bi-file-earmark-text fs-5"></i>
                        </div>
                        <h5 style={{ color: primaryColor, fontWeight: '600', margin: 0 }}>
                          Documents de la société <span className="text-danger">*</span>
                        </h5>
                      </div>
                      
                      {errors.files && (
                        <div className="alert alert-warning border-warning mb-4" style={{ 
                          backgroundColor: '#fff8e1', borderColor: '#ffc107', color: '#ff8f00', borderRadius: '8px'
                        }}>
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          {errors.files}
                        </div>
                      )}
                      
                      <div className="row g-4">
                        <div className="col-12 col-md-6">
                          <div className="card h-100" style={{ 
                            borderRadius: '12px', border: `1px solid ${borderColor}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                          }}>
                            <div className="card-header" style={{ 
                              backgroundColor: primaryColor, color: 'white', borderRadius: '12px 12px 0 0',
                              borderBottom: `2px solid ${accentColor}`
                            }}>
                              <h6 className="mb-0">
                                <i className="bi bi-file-earmark-pdf me-2"></i>
                                Document NIF
                              </h6>
                            </div>
                            <div className="card-body">
                              <input
                                type="file"
                                className={`form-control ${errors.nif_file ? 'is-invalid' : ''}`}
                                name="nif_file"
                                onChange={handleFileChange}
                                accept=".pdf,.jpg,.jpeg,.png"
                                disabled={loading}
                                style={{ 
                                  borderRadius: '8px', borderColor: errors.nif_file ? '#f44336' : borderColor,
                                  padding: '10px'
                                }}
                              />
                              {errors.nif_file && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.nif_file}</div>}
                              {files.nif_file && (
                                <div className="alert alert-success border-success py-2 mt-2" style={{ 
                                  backgroundColor: '#e8f5e9', borderRadius: '6px'
                                }}>
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <div>
                                      <strong style={{ color: '#2e7d32' }}>{files.nif_file.name}</strong>
                                      <div className="small text-muted">Taille: {formatFileSize(files.nif_file.size)}</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-12 col-md-6">
                          <div className="card h-100" style={{ 
                            borderRadius: '12px', border: `1px solid ${borderColor}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                          }}>
                            <div className="card-header" style={{ 
                              backgroundColor: primaryColor, color: 'white', borderRadius: '12px 12px 0 0',
                              borderBottom: `2px solid ${accentColor}`
                            }}>
                              <h6 className="mb-0">
                                <i className="bi bi-file-earmark-text me-2"></i>
                                Document RCCM
                              </h6>
                            </div>
                            <div className="card-body">
                              <input
                                type="file"
                                className={`form-control ${errors.rccm_file ? 'is-invalid' : ''}`}
                                name="rccm_file"
                                onChange={handleFileChange}
                                accept=".pdf,.jpg,.jpeg,.png"
                                disabled={loading}
                                style={{ 
                                  borderRadius: '8px', borderColor: errors.rccm_file ? '#f44336' : borderColor,
                                  padding: '10px'
                                }}
                              />
                              {errors.rccm_file && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.rccm_file}</div>}
                              {files.rccm_file && (
                                <div className="alert alert-success border-success py-2 mt-2" style={{ 
                                  backgroundColor: '#e8f5e9', borderRadius: '6px'
                                }}>
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <div>
                                      <strong style={{ color: '#2e7d32' }}>{files.rccm_file.name}</strong>
                                      <div className="small text-muted">Taille: {formatFileSize(files.rccm_file.size)}</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row g-4 mt-1">
                        <div className="col-12">
                          <div className="card h-100" style={{
                            borderRadius: '12px', border: `1px solid ${borderColor}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                          }}>
                            <div className="card-header" style={{
                              backgroundColor: primaryColor, color: 'white', borderRadius: '12px 12px 0 0',
                              borderBottom: `2px solid ${accentColor}`
                            }}>
                              <h6 className="mb-0">
                                <i className="bi bi-image me-2"></i>
                                Logo de la société <span className="fw-normal">(facultatif)</span>
                              </h6>
                            </div>
                            <div className="card-body">
                              <div className="d-flex align-items-center gap-3 flex-wrap">
                                {logoPreview && (
                                  <img
                                    src={logoPreview}
                                    alt="Aperçu logo"
                                    style={{ height: '60px', width: '60px', objectFit: 'contain', borderRadius: '8px', border: `1px solid ${borderColor}` }}
                                  />
                                )}
                                <div className="flex-grow-1">
                                  <input
                                    type="file"
                                    className={`form-control ${errors.logo_file ? 'is-invalid' : ''}`}
                                    name="logo_file"
                                    onChange={handleLogoChange}
                                    accept="image/*"
                                    disabled={loading}
                                    style={{
                                      borderRadius: '8px', borderColor: errors.logo_file ? '#f44336' : borderColor,
                                      padding: '10px'
                                    }}
                                  />
                                  {errors.logo_file && <div className="invalid-feedback" style={{ fontSize: '0.85rem' }}>{errors.logo_file}</div>}
                                  <div className="small text-muted mt-1">Ce logo apparaîtra sur votre tableau de bord et vos rapports PDF. Formats : JPG, PNG (max 3 Mo).</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Boutons */}
                    <div className="d-flex justify-content-between align-items-center pt-4 border-top" style={{ borderColor: borderColor }}>
                      <Link to="/login" className="text-decoration-none fw-medium d-flex align-items-center" style={{ 
                        color: primaryColor, transition: 'all 0.2s'
                      }}>
                        <i className="bi bi-arrow-left me-2"></i>
                        Déjà un compte ? Se connecter
                      </Link>
                      
                      <button
                        type="submit"
                        className="btn fw-semibold px-4"
                        disabled={loading || passwordValidation.match === false}
                        style={{ 
                          backgroundColor: loading || passwordValidation.match === false ? '#B0BEC5' : primaryColor,
                          borderColor: loading || passwordValidation.match === false ? '#B0BEC5' : primaryColor,
                          color: 'white', padding: "12px 32px", borderRadius: '8px',
                          fontSize: '1rem', transition: 'all 0.3s ease', 
                          opacity: (loading || passwordValidation.match === false) ? 0.8 : 1,
                          cursor: (loading || passwordValidation.match === false) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Inscription en cours...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Créer mon compte
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '16px', border: 'none' }}>
              <div className="modal-header" style={{ 
                backgroundColor: modalContent.type === 'success' ? '#e8f5e9' : 
                                 modalContent.type === 'error' ? '#ffebee' : cardBgColor,
                borderBottom: `2px solid ${modalContent.type === 'success' ? '#4caf50' : 
                                                modalContent.type === 'error' ? '#f44336' : accentColor}`,
                borderRadius: '16px 16px 0 0'
              }}>
                <h5 className="modal-title" style={{ 
                  color: modalContent.type === 'success' ? '#2e7d32' : 
                         modalContent.type === 'error' ? '#c62828' : primaryColor
                }}>
                  <i className={`bi ${
                    modalContent.type === 'success' ? 'bi-check-circle-fill' : 
                    modalContent.type === 'error' ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'
                  } me-2`}></i>
                  {modalContent.title}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body py-4">
                <div style={{ fontSize: '1rem', lineHeight: '1.6', color: textColor }}>
                  {modalContent.message}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={() => setShowModal(false)}>
                  {modalContent.type === 'success' ? 'Fermer' : 'Compris'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RegisterPage;